import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { forceLogout, hasExpiredSession, watchSessionExpiry } from './utils/auth';
import { AssetFormProvider } from './contexts/AssetFormContext';
import { ReferenceDataProvider } from './contexts/ReferenceDataContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
// Import global styles
import './styles/Layout.css';
import './styles/Table.css';
import './styles/Forms.css';
import './styles/Modals.css';
import './styles/Handover.css';
import './styles/Home.css';
import './styles/Login.css';
import './styles/Responsive.css';
import './styles/Dashboard.css';
import './styles/Buttons.css';

// Import pages
import LoginPage from './pages/Auth/LoginPage';
import AdminDashboardPage from './pages/Admin/AdminDashboardPage';
import CheckoutPage from './pages/Admin/CheckoutPage';
import CheckoutFormPage from './pages/Admin/CheckoutFormPage';
import CheckinPage from './pages/Admin/CheckinPage';
import CheckinFormPage from './pages/Admin/CheckinFormPage';
import AcknowledgementsPage from './pages/Admin/AcknowledgementsPage';
import AddAssetPage from './pages/Admin/AddAssetPage';
import EditAssetPage from './pages/Admin/EditAssetPage';
import AssetListPage from './pages/Assets/AssetListPage';
import PeopleListPage from './pages/People/PeopleListPage';
import AddPersonPage from './pages/People/AddPersonPage';
import PersonDetailPage from './pages/People/PersonDetailPage';
import UserPermissionsPage from './pages/People/UserPermissionsPage';
import DepartmentsListPage from './pages/Departments/DepartmentsListPage';
import AddDepartmentPage from './pages/Departments/AddDepartmentPage';
import SitesListPage from './pages/Sites/SitesListPage';
import AddSitePage from './pages/Sites/AddSitePage';
import CategoriesListPage from './pages/Categories/CategoriesListPage';
import AddCategoryPage from './pages/Categories/AddCategoryPage';
import LocationsListPage from './pages/Locations/LocationsListPage';
import AddLocationPage from './pages/Locations/AddLocationPage';
import UserRolesPage from './pages/Settings/UserRolesPage';
import GeneralSettingsPage from './pages/Settings/GeneralSettingsPage';
import GeneralSettingsDetailPage from './pages/Settings/GeneralSettingsDetailPage';
import ColumnSetupPage from './pages/Settings/ColumnSetupPage';
import JobTypesPage from './pages/Settings/JobTypesPage';
import FeedbackParametersPage from './pages/Settings/FeedbackParametersPage';
import ReportsDashboardPage from './pages/Reports/ReportsDashboardPage';
import SiteWiseReportPage from './pages/Reports/SiteWiseReportPage';
import CategoryWiseReportPage from './pages/Reports/CategoryWiseReportPage';
import EmployeeHistoryReportPage from './pages/Reports/EmployeeHistoryReportPage';
import AssetHistoryReportPage from './pages/Reports/AssetHistoryReportPage';
import EmployeeDashboardPage from './pages/Employee/EmployeeDashboardPage';
import MyAssetHandoverPage from './pages/Employee/MyAssetHandoverPage';
import MyAssetsPage from './pages/Employee/MyAssetsPage';
import AcknowledgePage from './pages/Public/AcknowledgePage';
import EmployeeProfilePage from './pages/Employee/EmployeeProfilePage';
import EmployeeNotificationsPage from './pages/Employee/EmployeeNotificationsPage';
import EmployeeAssetDetailPage from './pages/Employee/EmployeeAssetDetailPage';
import EmployeeTransferFormPage from './pages/Employee/EmployeeTransferFormPage';
import AssetDetailPage from './pages/Assets/AssetDetailPage';
import TrashPage from './pages/Admin/TrashPage';
import TeamsEventsPage from './pages/Admin/TeamsEventsPage';
import AddTeamEventPage from './pages/Admin/AddTeamEventPage';
import EditTeamEventPage from './pages/Admin/EditTeamEventPage';
import CopyTeamEventPage from './pages/Admin/CopyTeamEventPage';
import EventFeedbackPage from './pages/Admin/EventFeedbackPage';
import EmployeeFeedbackReportPage from './pages/Admin/EmployeeFeedbackReportPage';
import ProjectsListPage from './pages/Projects/ProjectsListPage';
import TasksListPage from './pages/Tasks/TasksListPage';
import TaskViewPage from './pages/Tasks/TaskViewPage';
import ProjectViewPage from './pages/Projects/ProjectViewPage';
import FeatureViewPage from './pages/Projects/FeatureViewPage';
import AddProjectPage from './pages/Projects/AddProjectPage';
import SummaryReportPage from './pages/Reports/SummaryReportPage';
import { PROJECT_MANAGEMENT_PATHS, getProjectManagementPaths } from './modules/projectManagement/config';
import TeamFeedbackReportPage from './pages/Admin/TeamFeedbackReportPage';
import PerformanceReportPage from './pages/Admin/PerformanceReportPage';
import Matches from './pages/Admin/Matches';
import MatchFeedbackViewPage from './pages/Admin/MatchFeedbackViewPage';
import SupervisorFeedbackPage from './pages/Admin/SupervisorFeedbackPage';
import SupervisorFeedbackViewPage from './pages/Admin/SupervisorFeedbackViewPage';
import TeamFeedbackViewPage from './pages/Admin/TeamFeedbackViewPage';
import {
  RequireAssetReportsAccess,
  RequirePerformanceReportsAccess,
} from './components/reports/ReportRouteGuards';

// Import layouts
import AdminLayout from './components/layout/AdminLayout';
import EmployeeLayout from './components/layout/EmployeeLayout';

// Protected Route Component — same normalization as Settings → User Roles matrix keys
const normalizeRole = (role) =>
  String(role || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, '');

const matchesRequiredRole = (userRole, requiredRole) => {
  const normalizedUserRole = normalizeRole(userRole);

  if (Array.isArray(requiredRole)) {
    return requiredRole.map(normalizeRole).includes(normalizedUserRole);
  }

  return normalizedUserRole === normalizeRole(requiredRole);
};

/**
 * Ends the session the moment the JWT lapses, without waiting for the user to make a request.
 * Mounted once, above the routes, so it runs for the whole life of the tab.
 */
const SessionExpiryWatcher = () => {
  useEffect(() => watchSessionExpiry(() => forceLogout({ expired: true })), []);
  return null;
};

const ProtectedRoute = ({ children, requiredRole }) => {
  const userStr = sessionStorage.getItem('user');
  
  if (!userStr) {
    return <Navigate to="/login" replace />;
  }

  // An expired token is no longer a session: clear it out rather than rendering the app around it.
  if (hasExpiredSession()) {
    forceLogout({ expired: true });
    return <Navigate to="/login" replace />;
  }
  
  try {
    const user = JSON.parse(userStr);
    
    // Check if user has token and is authenticated
    if (!user.token || !user.isAuthenticated) {
      return <Navigate to="/login" replace />;
    }
    
    // Check role if required
    if (requiredRole && !matchesRequiredRole(user.role, requiredRole)) {
      return <Navigate to="/login" replace />;
    }
    
    return children;
  } catch (error) {
    return <Navigate to="/login" replace />;
  }
};

const EMPLOYEE_PROJECT_MANAGEMENT_PATHS = getProjectManagementPaths('employee');

function App() {
  return (
    <AssetFormProvider>
      <Router>
        <ReferenceDataProvider>
        <SessionExpiryWatcher />
        <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/acknowledge/:token" element={<AcknowledgePage />} />
        
        {/* Admin Routes */}
        <Route path="/admin/*" element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout>
              <Routes>
                <Route path="dashboard" element={<AdminDashboardPage />} />
                <Route path="teams-events" element={<TeamsEventsPage />} />
                <Route path="matches" element={<Matches />} />
                <Route path="matches/:matchId/view-feedback" element={<MatchFeedbackViewPage />} />
                <Route path="matches/:matchId/supervisor-feedback/:eventTeamId" element={<SupervisorFeedbackPage />} />
                <Route path="matches/:matchId/view-supervisor-feedback" element={<SupervisorFeedbackViewPage />} />
                <Route path="matches/:matchId/feedback/:id" element={<EventFeedbackPage />} />
                <Route path="teams-events/:id/feedback" element={<EventFeedbackPage />} />
                <Route path="teams-events/:eventTeamId/view-feedback" element={<TeamFeedbackViewPage />} />
                <Route path="add-team" element={<AddTeamEventPage variant="add" />} />
                <Route path="edit-team" element={<EditTeamEventPage />} />
                <Route path="copy-team" element={<CopyTeamEventPage />} />
                <Route path="projects" element={<Navigate to={PROJECT_MANAGEMENT_PATHS.projects} replace />} />
                <Route path="projects/add" element={<AddProjectPage />} />
                <Route path="projects/:id" element={<ProjectViewPage />} />
                <Route path="tasks" element={<Navigate to={PROJECT_MANAGEMENT_PATHS.tasks} replace />} />
                <Route path="tasks/:taskId" element={<TaskViewPage />} />
                <Route path="project-management/projects" element={<ProjectsListPage />} />
                <Route path="project-management/projects/:id" element={<ProjectViewPage />} />
                <Route path="project-management/projects/:id/features/:featureId" element={<FeatureViewPage />} />
                <Route path="project-management/tasks" element={<TasksListPage />} />
                <Route path="project-management/tasks/:taskId" element={<TaskViewPage />} />
                <Route path="project-management/summary-report" element={<SummaryReportPage />} />
                <Route path="assets" element={<AssetListPage />} />
                <Route path="assets/add" element={<AddAssetPage />} />
                <Route path="assets/:id/edit" element={<EditAssetPage />} />
                <Route path="assets/:id" element={<AssetDetailPage />} />
                <Route path="people" element={<PeopleListPage />} />
                <Route path="people/add" element={<AddPersonPage />} />
                <Route path="people/:id" element={<PersonDetailPage />} />
                <Route path="people/:id/edit" element={<AddPersonPage />} />
                <Route path="people/:id/permissions" element={<UserPermissionsPage />} />
                <Route path="sites" element={<SitesListPage />} />
                <Route path="sites/add" element={<AddSitePage />} />
                <Route path="sites/:id/edit" element={<AddSitePage />} />
                <Route path="departments" element={<DepartmentsListPage />} />
                <Route path="departments/add" element={<AddDepartmentPage />} />
                <Route path="departments/:id/edit" element={<AddDepartmentPage />} />
                <Route path="categories" element={<CategoriesListPage />} />
                <Route path="categories/add" element={<AddCategoryPage />} />
                <Route path="categories/:id/edit" element={<AddCategoryPage />} />
                <Route path="locations" element={<LocationsListPage />} />
                <Route path="locations/add" element={<AddLocationPage />} />
                <Route path="locations/:id/edit" element={<AddLocationPage />} />
                <Route path="checkout" element={<CheckoutPage />} />
                <Route path="checkout/form" element={<CheckoutFormPage />} />
                <Route path="checkin" element={<CheckinPage />} />
                <Route path="checkin/form" element={<CheckinFormPage />} />
                <Route path="my-asset-handover" element={<MyAssetHandoverPage />} />
                <Route path="my-asset-handover/:handoverId" element={<EmployeeAssetDetailPage />} />
                <Route path="my-assets" element={<MyAssetsPage />} />
                <Route path="transfer-assets/form" element={<EmployeeTransferFormPage />} />
                <Route path="acknowledgements" element={<AcknowledgementsPage />} />
                <Route path="notifications" element={<EmployeeNotificationsPage />} />
                <Route path="reports" element={<ReportsDashboardPage />} />
                <Route
                  path="employee-feedback-report"
                  element={
                    <RequirePerformanceReportsAccess>
                      <EmployeeFeedbackReportPage />
                    </RequirePerformanceReportsAccess>
                  }
                />
                <Route
                  path="team-feedback-report"
                  element={
                    <RequirePerformanceReportsAccess>
                      <TeamFeedbackReportPage />
                    </RequirePerformanceReportsAccess>
                  }
                />
                <Route
                  path="performance-report"
                  element={
                    <RequirePerformanceReportsAccess>
                      <PerformanceReportPage />
                    </RequirePerformanceReportsAccess>
                  }
                />
                <Route
                  path="reports/site-wise"
                  element={
                    <RequireAssetReportsAccess>
                      <SiteWiseReportPage />
                    </RequireAssetReportsAccess>
                  }
                />
                <Route
                  path="reports/category-wise"
                  element={
                    <RequireAssetReportsAccess>
                      <CategoryWiseReportPage />
                    </RequireAssetReportsAccess>
                  }
                />
                <Route
                  path="reports/employee-history"
                  element={
                    <RequireAssetReportsAccess>
                      <EmployeeHistoryReportPage />
                    </RequireAssetReportsAccess>
                  }
                />
                <Route
                  path="reports/asset-history"
                  element={
                    <RequireAssetReportsAccess>
                      <AssetHistoryReportPage />
                    </RequireAssetReportsAccess>
                  }
                />
                <Route path="trash" element={<TrashPage />} />
                <Route path="settings" element={<GeneralSettingsPage />} />
                <Route path="settings/general" element={<GeneralSettingsDetailPage />} />
                <Route path="settings/user-roles" element={<UserRolesPage />} />
                <Route path="settings/columns" element={<ColumnSetupPage />} />
                <Route path="settings/job-types" element={<JobTypesPage />} />
                <Route path="settings/feedback-parameters" element={<FeedbackParametersPage />} />
                <Route path="profile" element={<EmployeeProfilePage />} />
                <Route index element={<AdminDashboardPage />} />
                {/* Add more admin routes here */}
              </Routes>
            </AdminLayout>
          </ProtectedRoute>
        } />
        
        {/* Employee Routes */}
        <Route path="/employee/*" element={
          <ProtectedRoute requiredRole={['employee', 'teamleader', 'hod', 'team-leader', 'manager']}>
            <EmployeeLayout>
              <Routes>
                <Route path="teams-events" element={<TeamsEventsPage />} />
                <Route path="matches" element={<Matches />} />
                <Route path="matches/:matchId/view-feedback" element={<MatchFeedbackViewPage />} />
                <Route path="matches/:matchId/supervisor-feedback/:eventTeamId" element={<SupervisorFeedbackPage />} />
                <Route path="matches/:matchId/view-supervisor-feedback" element={<SupervisorFeedbackViewPage />} />
                <Route path="matches/:matchId/feedback/:id" element={<EventFeedbackPage />} />
                <Route path="teams-events/:eventTeamId/view-feedback" element={<TeamFeedbackViewPage />} />
                <Route path="teams-events/:id/feedback" element={<EventFeedbackPage />} />
                <Route path="add-team" element={<AddTeamEventPage variant="add" />} />
                <Route path="edit-team" element={<EditTeamEventPage />} />
                <Route path="copy-team" element={<CopyTeamEventPage />} />
                <Route path="my-asset-handover" element={<MyAssetHandoverPage />} />
                <Route path="my-asset-handover/:handoverId" element={<EmployeeAssetDetailPage />} />
                <Route path="my-assets" element={<MyAssetsPage />} />
                <Route path="assets" element={<AssetListPage />} />
                <Route path="assets/add" element={<AddAssetPage />} />
                <Route path="assets/:id/edit" element={<EditAssetPage />} />
                <Route path="transfer-assets/form" element={<EmployeeTransferFormPage />} />
                <Route path="assets/:id" element={<AssetDetailPage />} />
                <Route path="people" element={<PeopleListPage />} />
                <Route path="people/add" element={<AddPersonPage />} />
                <Route path="people/:id" element={<PersonDetailPage />} />
                <Route path="people/:id/edit" element={<AddPersonPage />} />
                <Route path="people/:id/permissions" element={<UserPermissionsPage />} />
                <Route path="dashboard" element={<EmployeeDashboardPage />} />
                <Route path="profile" element={<EmployeeProfilePage />} />
                <Route path="notifications" element={<EmployeeNotificationsPage />} />
                <Route path="projects" element={<Navigate to={EMPLOYEE_PROJECT_MANAGEMENT_PATHS.projects} replace />} />
                <Route path="project-management/projects" element={<ProjectsListPage />} />
                <Route path="project-management/projects/:id" element={<ProjectViewPage />} />
                <Route path="project-management/projects/:id/features/:featureId" element={<FeatureViewPage />} />
                <Route path="tasks" element={<Navigate to={EMPLOYEE_PROJECT_MANAGEMENT_PATHS.tasks} replace />} />
                <Route path="project-management/tasks" element={<TasksListPage />} />
                <Route path="project-management/tasks/:taskId" element={<TaskViewPage />} />
                <Route path="project-management/summary-report" element={<SummaryReportPage />} />
                <Route path="reports" element={<ReportsDashboardPage />} />
                <Route
                  path="employee-feedback-report"
                  element={
                    <RequirePerformanceReportsAccess>
                      <EmployeeFeedbackReportPage />
                    </RequirePerformanceReportsAccess>
                  }
                />
                <Route
                  path="team-feedback-report"
                  element={
                    <RequirePerformanceReportsAccess>
                      <TeamFeedbackReportPage />
                    </RequirePerformanceReportsAccess>
                  }
                />
                <Route
                  path="performance-report"
                  element={
                    <RequirePerformanceReportsAccess>
                      <PerformanceReportPage />
                    </RequirePerformanceReportsAccess>
                  }
                />
                <Route
                  path="reports/site-wise"
                  element={
                    <RequireAssetReportsAccess>
                      <SiteWiseReportPage />
                    </RequireAssetReportsAccess>
                  }
                />
                <Route
                  path="reports/category-wise"
                  element={
                    <RequireAssetReportsAccess>
                      <CategoryWiseReportPage />
                    </RequireAssetReportsAccess>
                  }
                />
                <Route
                  path="reports/employee-history"
                  element={
                    <RequireAssetReportsAccess>
                      <EmployeeHistoryReportPage />
                    </RequireAssetReportsAccess>
                  }
                />
                <Route
                  path="reports/asset-history"
                  element={
                    <RequireAssetReportsAccess>
                      <AssetHistoryReportPage />
                    </RequireAssetReportsAccess>
                  }
                />
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="*" element={<Navigate to="/employee/dashboard" replace />} />
                {/* All non-admin roles share /employee/* and EmployeeDashboardPage at /employee/dashboard */}
              </Routes>
            </EmployeeLayout>
          </ProtectedRoute>
        } />
        
        {/* Default route - redirect to login */}
        <Route path="/" element={<LoginPage />} />
      </Routes>
        </ReferenceDataProvider>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      </Router>
    </AssetFormProvider>
  );
}

export default App;

